import { useState } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';
import RecordRTC from 'recordrtc';
import { useNavigate } from 'react-router-dom';
import { useNavigationTracking } from '../../../hooks/useNavigationTracking';

interface FeedbackScreenHoldProps {
  onNext: () => void;
  deferSave?: boolean;
  setFeedbackData?: (data: any) => void;
  navigateToProduct?: (
    productCode: string,
    preserveCustomerId?: boolean
  ) => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  feedbackSessionId?: string;
}

export default function FeedbackScreenHold({
  onNext,
  deferSave = false,
  setFeedbackData,
  navigateToProduct,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
  feedbackSessionId,
}: FeedbackScreenHoldProps) {
  const {
    backendBaseUrl,
    resolveMediaUrl,
    appState,
    setAppState,
    product,
    style,
    userEmail,
    sessionId,
    cycleCustomerId,
    setCycleCustomerId,
    isEventMode,
    consents,
    deviceData,
  } = useAppContext();
  const effectiveSessionId = feedbackSessionId || sessionId;
  const navigate = useNavigate();
  const { trackNavigation } = useNavigationTracking();
  const [recorder, setRecorder] = useState<any>(null);
  const [textReview, setTextReview] = useState<string>('');
  const [isTextMode, setIsTextMode] = useState<boolean>(false);
  const [showTextReview, setShowTextReview] = useState<boolean>(false);
  const isProductMode = true;

  const getMicrophoneInput = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const newRecorder = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/webm',
        checkForInactiveTracks: true,
      });
      newRecorder.startRecording();
      setRecorder(newRecorder);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopMicrophoneInput = async (): Promise<Blob | null> => {
    if (recorder) {
      return new Promise(resolve => {
        recorder.stopRecording(() => {
          const blob = recorder.getBlob();
          resolve(blob);
        });
      });
    }
    return null;
  };

  const sendReviewInBackground = (audioBlob?: Blob) => {
    if (isTextMode) {
      const payload: any = {
        product,
        text: textReview,
        userEmail,
        question: product?.product_indication,
        rating: 0,
        feedback_type: 'text',
        session_id: effectiveSessionId,
        customer_id: cycleCustomerId,
        consents,
        cycle_count: deviceData.cycle_count,
        codentify: deviceData.codentify,
        purchase_date: deviceData.purchase_date,
        adc: deviceData.adc,
        months: deviceData.months,
      };
      axios
        .post(`${backendBaseUrl}/send-to-insightgpt`, payload)
        .then(response => {
          if (response.data.customer_id) {
            setCycleCustomerId(response.data.customer_id);
          }
          if (deferSave && setFeedbackData && response.data.feedback_id) {
            setFeedbackData({
              textReview,
              isTextMode: true,
              microphoneInput: null,
              feedbackId: response.data.feedback_id,
            });
          }
        })
        .catch(error => console.error('Failed to send review:', error));
    } else {
      if (!audioBlob) return;
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording_${Date.now()}.webm`);
      axios
        .post(`${backendBaseUrl}/transcribe-audio`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(transcriptionResponse => {
          const transcribedText = transcriptionResponse.data.transcription;
          const payload: any = {
            product,
            text: transcribedText,
            userEmail,
            question: product?.product_indication,
            rating: 0,
            feedback_type: 'audio',
            session_id: effectiveSessionId,
            customer_id: cycleCustomerId,
            consents,
            cycle_count: deviceData.cycle_count,
            codentify: deviceData.codentify,
            purchase_date: deviceData.purchase_date,
          };
          return axios
            .post(`${backendBaseUrl}/send-to-insightgpt`, payload)
            .then(response => {
              if (response.data.customer_id) {
                setCycleCustomerId(response.data.customer_id);
              }
              if (deferSave && setFeedbackData && response.data.feedback_id) {
                setFeedbackData({
                  textReview: transcribedText,
                  isTextMode: true,
                  microphoneInput: null,
                  feedbackId: response.data.feedback_id,
                });
              }
            });
        })
        .catch(error => console.error('Failed to send review:', error));
    }
  };

  const handleMouseDown = async () => {
    if (appState === 'initial') {
      setAppState('recording');
      await getMicrophoneInput();
    }
  };

  const handleMouseUp = async () => {
    if (appState === 'recording' && recorder) {
      const blob = await stopMicrophoneInput();
      if (deferSave && setFeedbackData && !isTextMode) {
        setFeedbackData({ microphoneInput: blob, isTextMode: false });
      } else {
        sendReviewInBackground(blob || undefined);
      }
      onNext();
      setAppState('initial');
    }
  };

  const handleSendTextReview = () => {
    sendReviewInBackground();
    onNext();
    setAppState('initial');
    setShowTextReview(false);
    setTextReview('');
    setIsTextMode(false);
    setRecorder(null);
  };

  const handleSkipToProduct = () => {
    if (!product?.redirect_product) return;
    trackNavigation({
      from_screen: 'feedback-hold',
      to_screen: 'skip_to_product',
      navigation_type: 'skip',
      screen_index: 0,
      total_screens: 1,
    });
    if (navigateToProduct) {
      navigateToProduct(
        product.redirect_product,
        isEventMode || Boolean(product.redirect_with_same_customer_id)
      );
    } else {
      navigate(`/?product=${product.redirect_product}`);
    }
  };

  const handleSkipNextScreen = () => {
    onNext();
  };

  const handleBackButton = () => {
    onBack?.();
  };

  const showSkipProduct = Boolean(
    product?.show_skip_product_button && product?.redirect_product
  );
  const showAnySkip =
    showSkipNextScreenButton || showSkipProduct || showBackButton;

  return (
    <div
      style={{
        margin: '0 auto',
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="review-actions"
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          paddingBottom: '8px',
        }}
      >
        {!showTextReview ? (
          <>
            {product?.show_click_and_talk_button !== false && (
              <div
                id="audio-review"
                className="review-block"
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 'clamp(16px, 2.5vh, 28px)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={resolveMediaUrl(
                      appState === 'initial'
                        ? style.recording_button_img
                        : style.holdrecording_button_img
                    )}
                    alt={
                      appState === 'initial'
                        ? 'Press and hold to record'
                        : 'Recording...'
                    }
                    style={{
                      maxHeight: 'min(12vh, 30vw, 120px)',
                      maxWidth: 'min(12vh, 30vw, 120px)',
                      marginTop: 'clamp(16px, 2.5vh, 28px)',
                      cursor: 'pointer',
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchEnd={handleMouseUp}
                  />
                  <div
                    style={{
                      marginTop: appState === 'initial' ? '2vh' : '10%',
                      fontWeight: 'bold',
                      fontSize: '100%',
                      color: appState === 'initial' ? '#07296A' : '#84071E',
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        appState === 'initial'
                          ? style.recording_indication || ''
                          : style.hold_recording_indication || '',
                    }}
                  />
                </div>
              </div>
            )}
            {appState === 'initial' && (
              <div
                className="button-block"
                style={{
                  flex: '0 0 auto',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop:
                    product?.show_click_and_talk_button !== false
                      ? isProductMode
                        ? 'clamp(20px, 3.5vh, 40px)'
                        : '16px'
                      : 'clamp(16px, 2.5vh, 28px)',
                  marginBottom: 'clamp(16px, 2vh, 24px)',
                }}
              >
                <button
                  style={{
                    height: '4.6vh',
                    minHeight: '36px',
                    width: 'min(34vh, 90vw)',
                    maxWidth: '420px',
                    borderRadius: '7px',
                    padding: '0 20px',
                    fontSize: '90%',
                    fontWeight: 'bold',
                    color: style.text_review_button_text_color || 'white',
                    background:
                      style.text_review_button_bg_color ||
                      'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow:
                      '0 5px 5px ' +
                      (style.text_review_button_shadow_color || '#FFFFFF'),
                  }}
                  onClick={() => {
                    setShowTextReview(true);
                    setTextReview('');
                    setIsTextMode(true);
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      style.text_review_button_indication ||
                      'Write Text Review',
                  }}
                ></button>
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className="text-review-block"
              style={{
                height: isProductMode ? '25vh' : '100%',
                marginTop: '1.6vh',
              }}
            >
              <textarea
                className="text-review-input"
                placeholder="Write your text review here..."
                value={textReview}
                onChange={e => setTextReview(e.target.value)}
                style={{
                  width: 'min(34vh, 90vw)',
                  maxWidth: '420px',
                  height: isProductMode ? '10vh' : '30vh',
                  minHeight: '80px',
                }}
              ></textarea>
            </div>
            <div
              className="button-block"
              style={{
                height: isProductMode ? '10vh' : '24.5%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <button
                disabled={!textReview.trim()}
                style={
                  textReview.trim()
                    ? {
                        height: '4.6vh',
                        minHeight: '36px',
                        width: 'min(34vh, 90vw)',
                        maxWidth: '420px',
                        borderRadius: '4px',
                        padding: '0 20px',
                        fontSize: '90%',
                        fontWeight: 'bold',
                        color: style.text_review_button_text_color || 'white',
                        background:
                          style.text_review_button_bg_color ||
                          'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow:
                          '0 5px 5px ' +
                          (style.text_review_button_shadow_color || '#FFFFFF'),
                        marginTop: '2vh',
                      }
                    : {
                        height: '4.6vh',
                        minHeight: '36px',
                        width: 'min(34vh, 90vw)',
                        maxWidth: '420px',
                        borderRadius: '4px',
                        padding: '0 20px',
                        fontSize: '90%',
                        fontWeight: 'bold',
                        color: '#07296A',
                        background: '#e4e5e9',
                        border: 'none',
                        cursor: 'not-allowed',
                        boxShadow: '0 5px 5px rgba(7, 41, 106, 0.5)',
                        marginTop: '2vh',
                      }
                }
                onClick={handleSendTextReview}
              >
                Send Text Review
              </button>
              <button
                onClick={() => {
                  setAppState('initial');
                  setShowTextReview(false);
                  setTextReview('');
                  setIsTextMode(false);
                  setRecorder(null);
                }}
                style={{
                  fontSize: '12px',
                  textDecoration: 'underline',
                  color: '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: '12px',
                }}
              >
                Back
              </button>
            </div>
          </>
        )}
        {showAnySkip && appState !== 'recording' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              marginTop: 'clamp(16px, 2.5vh, 32px)',
              flex: '0 0 auto',
            }}
          >
            {showBackButton && (
              <button
                type="button"
                onClick={handleBackButton}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.back_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: style?.back_button_indication || 'Back',
                  }}
                />
              </button>
            )}
            {showSkipNextScreenButton && (
              <button
                type="button"
                onClick={handleSkipNextScreen}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.skip_next_screen_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: style?.skip_next_screen_button_indication || 'Skip',
                  }}
                />
              </button>
            )}
            {showSkipProduct && (
              <button
                type="button"
                onClick={handleSkipToProduct}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.skip_product_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: showSkipNextScreenButton ? '0.8vh' : 0,
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      style?.skip_product_button_indication ||
                      'Skip to product',
                  }}
                />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
